import {
    IconButton,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import 'keen-slider/keen-slider.min.css'
import { useKeenSlider } from 'keen-slider/react'
import { intersection } from 'lodash'
import { FC, useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import PrevIcon from '../../../../assets/icons/ChevronLeft.svg'
import NextIcon from '../../../../assets/icons/ChevronRight.svg'
import { useProfileStore } from '../../../../store/storeProfile'
import { useRequestsStore } from '../../../../store/storeRequests'
import { useWrapperStore } from '../../../../store/storeWrapper'
import { ModelsCarouselIcon } from './ModelsCarouselIcon'

const firstTierModelsSlugs = [
    'VEO_3_1_FAST',
    'SORA_2',
    'FLUX_2_PRO',
    'MIDJOURNEY',
    'NANO_BANANA_PRO',
    'YANDEX_LATEST',
    'PERPLEXITY_SONAR',
    'GROK_4_1_FAST',
    'GEMINI3_PRO',
    'GPT5_2',
    'DEEPSEEK_R1',
    'CLAUDE_4_5_SONNET',
]
const secondTierModelsSlugs = [
    'OPENAI_O4_MINI',
    'GEMINI_FLASH_1.5_8B',
    'DEEPSEEK_V3.2',
    'GPT5_2_CHAT',
]

export const ModelsCarousel: FC = () => {
    const { breakpoints } = useTheme()
    const isMobile = useMediaQuery(breakpoints.down('md'))
    const [searchParams, setSearchParams] = useSearchParams()

    const [currentSlide, setCurrentSlide] = useState(0)

    const { modelListFull, setCurrentAgentAndAssistantIdDialog } = useWrapperStore((state) => state)
    const { setFile } = useRequestsStore((state) => state)
    const { isFullAccess, userTariff } = useProfileStore()

    const handleSelectModel = useCallback((slug: string) => {
        setCurrentAgentAndAssistantIdDialog({
            agent: slug,
            assistantId: null,
        })
        searchParams.delete('assistant_id')
        setSearchParams(searchParams)

        setFile([])
    }, [])

    const itemsMap = useMemo(() => {
        const availableModels = intersection(
            userTariff?.tariff?.restrictions?.available_models,
            firstTierModelsSlugs,
        )

        const finalAvailableModels = availableModels.length >= 3
            ? availableModels
            : [
                ...availableModels,
                ...intersection(
                    userTariff?.tariff?.restrictions?.available_models,
                    secondTierModelsSlugs,
                )
            ]

        const sortingOrder = new Map(modelListFull?.map((model, idx) => [model.slug.valueOf(), idx]))
        const sortedModels = finalAvailableModels.sort((a, b) => {
            return (sortingOrder?.get(a) ?? 100) - (sortingOrder?.get(b) ?? 100)
        })
        return sortedModels.map((slug) => {
            const modelData = modelListFull?.find((model) => model.slug === slug)

            return (
                <Tooltip
                    title={
                        <Stack p={1.5} gap={0.75}>
                            <Typography variant="caption2">{modelData?.title}</Typography>
                            {modelData?.is_restricted && (
                                <Typography variant="caption2" sx={{color: '#5A5F72'}}>
                                    Отключена в глобальных настройках
                                </Typography>
                            )}
                            {modelData?.description && (
                                <Typography variant="caption">{modelData?.description}</Typography>
                            )}
                        </Stack>
                    }
                    placement="bottom-end"
                    slotProps={{
                        tooltip: {
                            sx: {
                                maxWidth: '270px',
                                borderRadius: '8px',
                                backgroundColor: '#FFFFFF',
                                p: 0,
                                boxShadow: '0px 4px 19.2px 0px #0000001A',
                            },
                        },
                    }}>
                    <div className="keen-slider__slide">
                        <IconButton
                            disabled={modelData?.is_restricted}
                            onClick={() => handleSelectModel(slug)}
                            data-slug={slug}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '28px',
                                height: '28px',
                                p: 0,
                                '&.Mui-disabled': {
                                    opacity: '0.5',
                                },
                            }}>
                            <ModelsCarouselIcon agentKey={slug} />
                        </IconButton>
                    </div>
                </Tooltip>
            )
        })
    }, [modelListFull, handleSelectModel, isFullAccess])

    const itemsPerView = isMobile ? 7 : 8
    const isPaginationEnabled = itemsMap.length > itemsPerView
    const perView = isPaginationEnabled || !itemsMap.length ? itemsPerView : itemsMap.length

    const [sliderRef, instanceRef] = useKeenSlider({
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel)
        },
        mode: 'free',
        slides: {
            perView,
            spacing: 0,
        },
    })

    if (!modelListFull) {
        return (
            <Skeleton
                variant="rectangular"
                sx={{
                    width: '340px',
                    height: '30px',
                    borderRadius: '24px',
                    mt: 4,
                }}
            />
        )
    }

    const disablePrev = currentSlide === 0
    const disableNext =
        currentSlide > 0 &&
        currentSlide === (instanceRef.current?.track.details.slides.length ?? 0) - itemsPerView

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                width: '100%',
                maxWidth: `${perView * 30 + (isPaginationEnabled ? 80 : 0)}px`,
                mt: 4,
            }}>
            {isPaginationEnabled && (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation()
                        instanceRef.current?.prev()
                    }}
                    disabled={disablePrev}>
                    <PrevIcon />
                </IconButton>
            )}
            <div ref={sliderRef} className="keen-slider">
                {itemsMap}
            </div>
            {isPaginationEnabled && (
                <IconButton
                    onClick={(e) => {
                        e.stopPropagation()
                        instanceRef.current?.next()
                    }}
                    disabled={disableNext}>
                    <NextIcon />
                </IconButton>
            )}
        </Stack>
    )
}
